import { useContext, useEffect, useState } from 'react'

import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate } from 'react-router-dom'
import { Grid } from '@mui/material'
import * as Yup from 'yup'

import { cleanCpf, composePath, isCpfValid } from 'src/utils'
import { ModalInformation } from 'src/components/ModalInformation'
import { TextFieldMask } from 'src/components/TextFieldMask'
import { FileDetail } from 'src/components/FileDetail'
import { BoxButtons } from 'src/components/BoxButton'
import { ErrorText } from 'src/components/ErrorText'
import DataContext from 'src/contexts/DataContext'
import { Stepper } from 'src/components/Stepper'
import { Button } from 'src/components/Button'
import { useGetCandidateByCpfLazy } from 'src/services/backend'
import * as S from './styles'
import { emitNotificationEvent } from 'src/events'

const DocumentsPage = () => {
  const navigate = useNavigate()
  const MAX_FILE_SIZE = 20 * 1024 * 1024 // 50MB

  const toasts = {
    showGetPersonByCpf: () =>
      emitNotificationEvent({
        id: 'getPersonByCpf',
        type: 'error',
        title: 'Ops!',
        message: 'Ocorreu um erro técnico ao consultar o cpf.',
        isIndeterminate: true,
        textButton: 'Entendi',
      }),
    showNoMoreAvailableCourses: (message: string) =>
      emitNotificationEvent({
        id: 'noMoreAvailableCourses',
        type: 'error',
        title: 'Ops!',
        message: message,
        isIndeterminate: true,
        textButton: 'Entendi',
      }),
  }

  const validationScheema = Yup.object().shape(
    {
      cpf: Yup.string()
        .required('CPF do(a) candidato(a) é obrigatório')
        .test('is-cpf-valid', 'CPF do(a) candidato(a) é inválido', (value) =>
          isCpfValid(value)
        ),
      uploadFile: Yup.mixed()
        //.required('O campo de comprovante de escolaridade é obrigatório')
        // .test(
        //   'is-valid-type',
        //   "Formato de imagem inválido, utilize '.png', '.jpg', '.jpeg' ou '.pdf",
        //   (value: any) => isValidFileType(value?.name?.toLowerCase())
        // )
        .nullable()
        .notRequired()

        .when('uploadFile', {
          is: (value: any) => isValidFileType(value?.name?.toLowerCase()),
          then: (value) => value,
        })
        .when({
          is: (val: any) => val instanceof File,
          then: (schema) =>
            schema.test(
              'is-valid-size',
              'Tamanho máximo permitido para o arquivo é 20MB',
              (value: any) => value?.size <= MAX_FILE_SIZE
            ),
        }),
    },
    [['uploadFile', 'uploadFile']]
  )

  const defaultValues = {
    cpf: '',
    uploadFile: undefined,
  }

  const {
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationScheema),
  })

  const [getCandidateByCpf, getCandidateByCpfResult] =
    useGetCandidateByCpfLazy()

  const { registration, setRegistration } = useContext(DataContext)
  const currentStep = registration.getStep(registration, 'documents')

  const [open, setOpen] = useState(false)
  const [cpfExists, setCpfExists] = useState(false)

  function isValidFileType(fileName: string = '') {
    const validFileExtensions = ['png', 'jpg', 'jpeg', 'pdf']
    return validFileExtensions.indexOf(fileName.split('.').pop() || '') > -1
  }

  const checkIfCpfExists = (cpf: string) => {
    if (isCpfValid(cpf) && registration.courseTypeId)
      getCandidateByCpf(cleanCpf(cpf), registration.courseTypeId)
  }

  const handleModalInformation = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const submitForm = (data: any) => {
    if (isFormValid() && !cpfExists) {
      if (isDirty) {
        registration.setFormData(setRegistration, 'documents', data)
        registration.setCpf(setRegistration, data.cpf)

        checkIfCpfExists(data.cpf)
      } else navigateToNextStep()
    }
  }

  const navigateToNextStep = () =>
    navigate(composePath('/dados-pessoais', `admissao/${registration.course}`))

  const nameImage = (watch('uploadFile') as any)?.name
  const sizeImage = () => {
    let size = (watch('uploadFile') as any)?.size
    let kilobites = Math.round(size / 1024)
    return kilobites.toString() + 'Kb'
  }

  const isFormValid = () => isValid || !Object.keys(errors)?.length

  const setFormData = (formData: any) => {
    Object.keys(formData).forEach((field) => {
      //@ts-ignore
      setValue(field, formData[field])
    })

    reset(defaultValues, {
      keepErrors: true,
      keepDirty: false,
      keepDirtyValues: false,
      keepValues: true,
      keepDefaultValues: false,
      keepIsSubmitted: true,
      keepTouched: false,
      keepIsValid: true,
      keepSubmitCount: true,
    }) // resetando alguns estados especificos do formulario

    if (formData?.cpf !== registration.cpf && registration.courseTypeId)
      getCandidateByCpf(cleanCpf(formData?.cpf), registration.courseTypeId)
  }

  useEffect(() => {
    if (!!currentStep?.formData) setFormData(currentStep.formData)
  }, [])

  useEffect(() => {
    if (getCandidateByCpfResult?.data?.response?.isCandidate) {
      setCpfExists(getCandidateByCpfResult?.data?.response?.isCandidate)
      if (getCandidateByCpfResult?.data?.message)
        toasts.showNoMoreAvailableCourses(
          getCandidateByCpfResult?.data?.message
        )
    }
  }, [getCandidateByCpfResult?.data])

  useEffect(() => {
    if (getCandidateByCpfResult?.data) {
      if (
        getCandidateByCpfResult?.data?.response?.isCandidate === false &&
        isDirty
      )
        navigateToNextStep()
      if (getCandidateByCpfResult?.data?.response?.isCandidate)
        navigate('/candidato/login')
    }
  }, [getCandidateByCpfResult?.data])

  // useEffect(() => {
  //   if (getCandidateByCpfResult?.error) {
  //     if (getCandidateByCpfResult.error === 'Pessoa não encontrada ')
  //       navigateToNextStep()
  //     else toasts.showGetPersonByCpf()
  //   }
  // }, [getCandidateByCpfResult?.error])

  return (
    <S.ContainerFlex onSubmit={handleSubmit(submitForm)}>
      <S.Content>
        <Stepper
          currentStepName={currentStep?.name}
          label={currentStep?.title}
          course={registration.course}
        />
        <S.Title>
          Preencha os dados abaixo e anexe o comprovante solicitado para
          prosseguir com a inscrição:
        </S.Title>
        <S.Typography>
          informe o cpf do(a) <span>candidato(a)</span>
        </S.Typography>
        <Grid container xs={12} spacing={2}>
          <Grid item lg={6} md={6} xs={12}>
            <Controller
              name="cpf"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldMask
                  value={field.value}
                  onChangeMask={(e) => {
                    setCpfExists(false)
                    field.onChange(e)
                  }}
                  error={!!fieldState.error}
                  mask="999.999.999-99"
                  label="CPF do(a) candidato(a)"
                  variant="outlined"
                />
              )}
            />
            <ErrorText message={errors.cpf && errors.cpf.message} />
            {cpfExists && (
              <ErrorText message="Já existe um(a) candidato(a) cadastrado com esse CPF" />
            )}
          </Grid>
          <Grid item lg={6} md={6} xs={12}>
            <S.ContainerInfo>
              <S.PriorityHigh />
              <S.TextInfo>
                O CPF do candidato é obrigatório, não utilizar CPF de terceiros.
                Sujeito a desclassificação.{' '}
                <S.ButtonInfo onClick={() => handleModalInformation()}>
                  Clique aqui e saiba mais
                </S.ButtonInfo>
              </S.TextInfo>
            </S.ContainerInfo>
          </Grid>
        </Grid>

        <S.Typography>
          <span>Anexe o comprovante de escolaridade do(a) candidato(a)</span>
        </S.Typography>

        <S.List>
          {registration.course == 'business' && (
            <S.ItemList>
              O(A) candidato(a) deve estar devidamente matriculado(a) no 5º ano
              do Ensino Fundamental I;
            </S.ItemList>
          )}
          {registration.course == 'tech' ||
            (['vet-lins', 'vet-amparo'].includes(registration.course!) && (
              <S.ItemList>
                O(A) candidato(a) deve estar devidamente matriculado(a) no 9º
                ano do Ensino Fundamental II;
              </S.ItemList>
            ))}
          <S.ItemList>
            O comprovante de escolaridade é um documento emitido pela escola na
            qual o(a) aluno(a) está devidamente matriculado(a), que indica a
            série que o(a) candidato(a) está cursando;
          </S.ItemList>
          <S.ItemList>
            Caso não seja possível anexar o documento neste momento, anexe na{' '}
            <b>Área do Candidato</b> ou envie para a Equipe de Admissão por meio
            do número de WhatsApp <b>(11)99732-4047</b>, após a conclusão da
            inscrição.
          </S.ItemList>
        </S.List>

        <Grid container xs={12} spacing={2}>
          <Grid item lg={6} md={6} xs={12}>
            <Grid container item xs={12} spacing={2}>
              <Grid item lg={12} md={12} xs={12}>
                <S.ContainerUploadInput>
                  <Controller
                    name="uploadFile"
                    control={control}
                    render={({ field }) => (
                      <S.DownloadButton variant="outlined" component="label">
                        <input
                          type="file"
                          accept=".png, .jpg, .jpeg, .pdf"
                          onChange={(event) => {
                            field.onChange(
                              event.currentTarget?.files?.length &&
                                event.currentTarget.files[0]
                            )
                          }}
                          hidden
                        />
                        <S.ButtonIcon>
                          <S.UploadIconStyled />
                        </S.ButtonIcon>
                        <S.Link>
                          {<span>Anexar comprovante de escolaridade</span>}
                        </S.Link>
                      </S.DownloadButton>
                    )}
                  />
                  <ErrorText
                    message={errors.uploadFile && errors.uploadFile.message}
                  />
                </S.ContainerUploadInput>
              </Grid>
              <Grid item lg={12} md={12} xs={12}>
                {!!watch('uploadFile') && (
                  <S.RenderDetail>
                    <FileDetail
                      name={nameImage}
                      size={sizeImage()}
                      onClickDelete={() => {
                        trigger('uploadFile')
                        setValue('uploadFile', undefined)
                      }}
                    />
                  </S.RenderDetail>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={6} md={6} xs={12}>
            <S.ContainerInfo>
              <S.PriorityHigh />
              <S.TextInfo>
                NÃO ANEXE RG, CPF ou captura de tela do aplicativo escolar, o
                documento correto é o comprovante de escolaridade.
              </S.TextInfo>
            </S.ContainerInfo>
          </Grid>
        </Grid>

        <BoxButtons
          onClickPrevius={() => navigate(`/admissao/${registration.course}`)}
          disableNextButton={!isFormValid() || cpfExists}
          isLoading={getCandidateByCpfResult.isLoading}
        />
      </S.Content>
      <ModalInformation title="Aviso" isOpen={open} onCloseModal={handleClose}>
        <S.ModalBox>
          <S.DescriptionModal>
            O CPF é um documento obrigatório para realizar a inscrição. Nesse
            caso, orientamos que os responsáveis solicitem a emissão do
            documento de forma digital ou em uma instituição pública conveniada.
            Assim que estiverem com o número de CPF emitido, retornem para
            prosseguir com o formulário.
          </S.DescriptionModal>

          <S.ModalBoxButtons>
            <Button variant={'outlined'} onClick={() => setOpen(false)}>
              Entendi
            </Button>
          </S.ModalBoxButtons>
        </S.ModalBox>
      </ModalInformation>
    </S.ContainerFlex>
  )
}

export { DocumentsPage }
